"use client"
import { useRef, useEffect, useState, useMemo } from 'react';
import { Calendar, Users, Briefcase, DollarSign, BarChart, UserPlus, CreditCard } from "lucide-react"
import EventDetailsTab from './EventDetailsTab'
import GuestListTab from './GuestListTab'
import EventAnalytics from '../components/EventAnalytics'
import EventPaymentsTab from '../components/EventPaymentsTab'
import ServiceBidsTab from './ServiceBidsTab';
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext";
import { useSuccessFailure } from "../context/SuccessFailureContext";
import { lazy, Suspense } from 'react';
import axios from 'axios';
import config from '../config';
import BudgetTracker from './BudgetTracker';

// Lazy load the EventPaymentsManager component
const EventPaymentsManager = lazy(() => import('../components/EventPaymentsManager'));

const EventTabs = ({
  selectedTab,
  setSelectedTab,
  isCreator,
  isLoggedIn,
  event,
  onRegister,
  fetchInvitationsAndGuests,
  guests = [],
  invitations = []
}) => {
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [vendorRequests, setVendorRequests] = useState([]);
  const [loadingVendorRequests, setLoadingVendorRequests] = useState(false);
  const containerRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);

  // Check scroll position to show/hide fade effects
  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Set up scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll(); // Initial check
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, []);

  // Get guest list and invitations from props
  const eventGuests = Array.isArray(guests) ? guests : [];
  const eventInvitations = Array.isArray(invitations) ? invitations : [];

  // Check if current user has been accepted to attend the event
  const userHasAcceptedInvitation = currentUser && eventInvitations.some(inv => 
    (inv.recipient?.id === currentUser.id || inv.recipient_email === currentUser.email) && 
    inv.status === 'accepted'
  );

  // console.log('🔍 USER ACCEPTANCE CHECK:', {
    // currentUser: currentUser,
    // userHasAcceptedInvitation,
    // eventInvitations: eventInvitations.map(inv => ({
    //   id: inv.id,
    //   recipient_email: inv.recipient_email,
    //   recipient_id: inv.recipient?.id,
    //   status: inv.status
    // }))
  // });

  // Fetch vendor requests for the event
  const fetchVendorRequests = async () => {
    if (!event?.id) return;

    setLoadingVendorRequests(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${config.API_BASE_URL}/bids/service-requests/${event.slug}/`,
        { headers }
      );
      setVendorRequests(response.data);
    } catch (error) {
      // console.error('Error fetching vendor requests:', error);
    } finally {
      setLoadingVendorRequests(false);
    }
  };

  // Fetch vendor requests when component mounts or event changes
  useEffect(() => {
    if (event?.slug) {
      fetchVendorRequests();
    }
  }, [event?.slug]);

  // Tab definitions
  const tabs = [
    {
      id: 'details',
      label: 'Event Details',
      icon: <Calendar size={18} />,
      show: true
    },
    {
      id: 'guests',
      label: 'Guest List',
      icon: <Users size={18} />,
      show: isCreator || isLoggedIn
    },
    {
      id: 'payments',
      label: 'My Payments',
      icon: <DollarSign size={18} />,
      show: isLoggedIn && !isCreator && event?.is_paid_event && (
        (event.has_accepted_invitation || userHasAcceptedInvitation)
      )
    },
    {
      id: 'budget',
      label: 'Budget',
      icon: <DollarSign size={18} />,
      show: isCreator
    },
    {
      id: 'service-requests',
      label: 'Service Bids',
      icon: <Briefcase size={18} />,
      show: isCreator
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <BarChart size={18} />,
      show: isCreator
    },
    {
      id: 'payment-management',
      label: 'Payments',
      icon: <CreditCard size={18} />,
      show: isCreator && event?.is_paid_event
    }
  ].filter(tab => tab.show);

  // Tab content rendering
  const renderTabContent = () => {
    if (!event) return <div>Loading event details...</div>;

    switch (selectedTab) {
      case 'details':
        return <EventDetailsTab event={event} loading={!event} />;
      case 'guests':
        return (
          <GuestListTab
            event={event}
            invitations={eventInvitations}
            guests={eventGuests}
            isCreator={isCreator}
            isLoggedIn={isLoggedIn}
            user={currentUser}
            fetchInvitationsAndGuests={fetchInvitationsAndGuests}
          />
        );
      case 'payments':
        return (
          <EventPaymentsTab
            event={event}
            currentUser={currentUser}
            onPaymentSuccess={() => setSelectedTab('details')}
          />
        );
      case 'payment-management':
        return (
          <Suspense fallback={<div>Loading payments...</div>}>
            <EventPaymentsManager
              event={event}
              showSuccessModal={(message) => console.log('Success:', message)}
              showFailureModal={(message) => console.error('Error:', message)}
            />
          </Suspense>
        );
      case 'budget':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Budget Tracker</h2>
            <BudgetTracker event={event} />
          </div>
        );
      case 'service-requests':
        return (
          <ServiceBidsTab
            event={event}
            serviceRequests={vendorRequests}
            setServiceRequests={setVendorRequests}
            isCreator={isCreator}
            user={currentUser}
            fetchServiceRequests={fetchVendorRequests}
            showSuccessModal={showSuccessModal}
            showFailureModal={showFailureModal}
          />
        );
      case 'analytics':
        return <EventAnalytics eventId={event.id} />;
      default:
        return null;
    }
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg p-1 md:p-2">
        <div className="relative">
          {/* Scrollable container */}
          <div
            ref={containerRef}
            className="overflow-x-auto pb-2 -mx-1 md:-mx-2 px-1 md:px-2 scrollbar-hide"
            style={{
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE and Edge
            }}
          >
            <div className="flex flex-nowrap gap-1 md:gap-2 w-max min-w-full">
              {tabs.map((tab, index) => (
                <div key={tab.id} className="flex-shrink-0">
                  <div className="flex items-center">
                    {/* Separator for small screens */}
                    {index > 0 && (
                      <span className="h-4 w-px bg-gray-200 mx-1 sm:mx-0 sm:hidden"></span>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedTab(tab.id);
                      }}
                      className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${selectedTab === tab.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                    >
                      <span className="text-base md:text-lg">{tab.icon}</span>
                      <span className="hidden sm:inline ml-1">{tab.label}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gradient fade effects */}
          {showLeftFade && (
            <div className="absolute left-0 top-0 bottom-0 w-8 md:w-12 bg-gradient-to-r from-white to-transparent pointer-events-none z-10"></div>
          )}
          {showRightFade && (
            <div className="absolute right-0 top-0 bottom-0 w-8 md:w-12 bg-gradient-to-l from-white to-transparent pointer-events-none z-10"></div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
        <Suspense fallback={<LoadingSpinner />}>
          {renderTabContent()}
        </Suspense>
      </div>
    </div>
  );
};

export default EventTabs;
